import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Building2 } from "lucide-react";
import api from "../services/api";
import StateSelect from "./StateSelect";

const emptyForm = {
  name: "",
  address: "",
  gstin: "",
  phone: "",
  stateCode: "",
  city: "",
  pincode: "",
  firmId: "",
  invoiceAtCustomerLevel: false,
  eInvoiceEnabled: false,
  transactionLockDate: "",
  parentDistributorId: "",
  multiCompanyEnabled: false,
  salesInvoicePrintId: "",
  // default numbering of sales invoices keyed in on the admin panel / mobile app
  invoicePrefix: "",
  defaultSalesVoucherTypeId: "",
  // margins of the distributor; a product's own margin %, when set, is used instead
  distributorMarginPercent: "",
  retailerMarginPercent: "",
  additionalMarginPercent: "",
};

/**
 * mode="mandatory": full-screen, blocking gate shown right after first login when no profile exists yet.
 * mode="edit": normal editable page, reachable anytime from the topbar Profile link.
 */
const DistributorProfile = ({ mode = "edit", onComplete }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  // after the lock date was set / changed: products whose closing stock is short of what open bills need
  const [stockShortages, setStockShortages] = useState(null);
  const [firms, setFirms] = useState([]);
  const [printFormats, setPrintFormats] = useState([]);
  const [salesVoucherTypes, setSalesVoucherTypes] = useState([]);

  useEffect(() => {
    if (mode !== "edit") return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await api.get("/api/v1/distributor-profile");
        if (data?.exists) {
          setFormData({
            name: data.name || "",
            address: data.address || "",
            gstin: data.gstin || "",
            phone: data.phone || "",
            stateCode: data.stateCode || "",
            city: data.city || "",
            pincode: data.pincode || "",
            firmId: data.firmId || "",
            invoiceAtCustomerLevel: data.invoiceAtCustomerLevel || false,
            eInvoiceEnabled: data.eInvoiceEnabled || false,
            transactionLockDate: data.transactionLockDate || "",
            parentDistributorId: data.parentDistributorId ?? "",
            multiCompanyEnabled: Boolean(data.multiCompanyEnabled),
            salesInvoicePrintId: data.salesInvoicePrintId ?? "",
            invoicePrefix: data.invoicePrefix || "",
            defaultSalesVoucherTypeId: data.defaultSalesVoucherTypeId ?? "",
            distributorMarginPercent: data.distributorMarginPercent ?? "",
            retailerMarginPercent: data.retailerMarginPercent ?? "",
            additionalMarginPercent: data.additionalMarginPercent ?? "",
          });
        }
      } catch (error) {
        toast.error(error.message || "Failed to load company profile");
      } finally {
        setLoading(false);
      }
    };
    load();
    api.get("/api/v1/firms", { size: 200 }).then((data) => setFirms(data?.content || [])).catch(() => {});
  }, [mode]);

  useEffect(() => {
    api.get("/api/v1/sales-invoice-prints").then((data) => setPrintFormats(data || [])).catch(() => {});
    if (mode === "edit")
      api
        .get("/api/v1/voucher-types/by-document-type", { documentType: "SALES_INVOICE" })
        .then((data) => setSalesVoucherTypes(Array.isArray(data) ? data : []))
        .catch(() => {});
  }, [mode]);

  // Until a format is picked, the default one is what prints, so show it as selected.
  const defaultPrintFormatId = printFormats.find((f) => f.defaultFormat)?.id ?? "";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // Every GSTIN's first 2 digits are that state's official GST code — pre-fill them so the user
  // only has to type the rest, replacing just the leading digits if some GSTIN was already typed.
  const handleStateSelect = (state) => {
    setFormData((prev) => ({ ...prev, gstin: state.gstCode + prev.gstin.slice(2) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await api.put("/api/v1/distributor-profile", {
        ...formData,
        firmId: formData.firmId ? Number(formData.firmId) : null,
        transactionLockDate: formData.transactionLockDate || null,
        parentDistributorId: formData.parentDistributorId === "" ? null : Number(formData.parentDistributorId),
        salesInvoicePrintId: formData.salesInvoicePrintId === "" ? null : Number(formData.salesInvoicePrintId),
        invoicePrefix: formData.invoicePrefix.trim() || null,
        defaultSalesVoucherTypeId: formData.defaultSalesVoucherTypeId === "" ? null : Number(formData.defaultSalesVoucherTypeId),
        distributorMarginPercent: formData.distributorMarginPercent === "" ? null : Number(formData.distributorMarginPercent),
        retailerMarginPercent: formData.retailerMarginPercent === "" ? null : Number(formData.retailerMarginPercent),
        additionalMarginPercent: formData.additionalMarginPercent === "" ? null : Number(formData.additionalMarginPercent),
      });
      toast.success("Company profile saved");
      const shortages = saved?.stockShortages || [];
      setStockShortages(shortages.length ? shortages : null);
      if (shortages.length) toast.warn(`${shortages.length} product(s) are short of stock — see the alert below. An SMS was sent to the company phone.`);
      onComplete?.();
    } catch (error) {
      toast.error(error.message || "Failed to save company profile");
    } finally {
      setSaving(false);
    }
  };

  const formBody = (
    <form onSubmit={handleSubmit}>
      <div className="form-grid">
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label>Company Name</label>
          <input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. Royal Mart Distributors" />
        </div>
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label>Address</label>
          <input name="address" value={formData.address} onChange={handleChange} required placeholder="Full company address" />
        </div>
        <div className="form-group">
          <label>GSTIN</label>
          <input name="gstin" value={formData.gstin} onChange={handleChange} placeholder="29ABCDE1234F1Z5" />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input name="phone" value={formData.phone} onChange={handleChange} placeholder="9876543210" />
        </div>
        <div className="form-group">
          <label>State Code</label>
          <StateSelect value={formData.stateCode} onChange={handleChange} onStateSelect={handleStateSelect} />
        </div>
        <div className="form-group">
          <label>City</label>
          <input name="city" value={formData.city} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Pincode</label>
          <input name="pincode" value={formData.pincode} onChange={handleChange} placeholder="e.g. 560001" />
        </div>
        {firms.length > 0 ? (
          <div className="form-group">
            <label>Firm (Group) — Super Admin only</label>
            <select name="firmId" value={formData.firmId} onChange={handleChange}>
              <option value="">No firm</option>
              {firms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="form-group">
          <label>Parent Distributor ID (group of companies) — Super Admin only</label>
          <input
            name="parentDistributorId"
            type="number"
            min="1"
            value={formData.parentDistributorId}
            onChange={handleChange}
            placeholder="Same ID on every company of the group"
          />
        </div>
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input type="checkbox" name="multiCompanyEnabled" checked={formData.multiCompanyEnabled} onChange={handleChange} />
            Multi-company dropdown in the mobile app and admin panel (switch between the group's companies) — Super Admin only
          </label>
        </div>
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input
              type="checkbox"
              name="invoiceAtCustomerLevel"
              checked={formData.invoiceAtCustomerLevel}
              onChange={handleChange}
            />
            Assign salesman at Customer Level
          </label>
          <div className="page-subtitle" style={{ marginTop: "4px" }}>
            When uploading sales orders/invoices: the Salesman Code/Name column in the file is always tried first.
            If it's off (Invoice Level) and neither resolves, the row is left unassigned. If it's on (Customer
            Level), unresolved rows fall back to the customer's own assigned beat and that beat's salesman.
          </div>
        </div>
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input
              type="checkbox"
              name="eInvoiceEnabled"
              checked={formData.eInvoiceEnabled}
              onChange={handleChange}
            />
            e-Invoicing Enabled — Super Admin only
          </label>
          <div className="page-subtitle" style={{ marginTop: "4px" }}>
            Intended for distributors above the e-invoicing turnover threshold. Set manually for now — automatic
            enablement based on annual turnover is not implemented yet.
          </div>
        </div>
        {printFormats.length > 0 ? (
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Sales Invoice Print Format</label>
            <select
              name="salesInvoicePrintId"
              value={formData.salesInvoicePrintId === "" ? defaultPrintFormatId : formData.salesInvoicePrintId}
              onChange={handleChange}
              style={{ maxWidth: "320px" }}
            >
              {printFormats.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            <div className="page-subtitle" style={{ marginTop: "4px" }}>
              The layout used when a sales invoice is printed.
            </div>
          </div>
        ) : null}
        {mode === "edit" ? (
          <>
            {[
              ["distributorMarginPercent", "Distributor Margin %"],
              ["retailerMarginPercent", "Retailer Margin %"],
              ["additionalMarginPercent", "Additional Margin %"],
            ].map(([name, label]) => (
              <div className="form-group" key={name}>
                <label>{label}</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.001"
                  name={name}
                  value={formData[name]}
                  onChange={(e) => setFormData((prev) => ({ ...prev, [name]: e.target.value }))}
                  placeholder="e.g. 1.45"
                  title="Used for products without their own margin % (e.g. the distributor margin on MRP-based lines)"
                />
              </div>
            ))}
            <div className="form-group">
              <label>Default Invoice Prefix</label>
              <input
                name="invoicePrefix"
                value={formData.invoicePrefix}
                onChange={(e) => setFormData((prev) => ({ ...prev, invoicePrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "") }))}
                maxLength={10}
                placeholder="INV"
                style={{ maxWidth: "160px" }}
              />
              <div className="page-subtitle" style={{ marginTop: "4px" }}>
                Sales invoices without a voucher type are numbered {formData.invoicePrefix || "INV"}/YY-YY/000001. Keep it to 3 characters for
                e-invoices (the GST portal accepts at most 16 characters).
              </div>
            </div>
            <div className="form-group">
              <label>Default Voucher Type (sales invoices)</label>
              <select name="defaultSalesVoucherTypeId" value={formData.defaultSalesVoucherTypeId} onChange={handleChange} style={{ maxWidth: "320px" }}>
                <option value="">Default ({formData.invoicePrefix || "INV"} numbering)</option>
                {salesVoucherTypes.map((vt) => (
                  <option key={vt.id} value={vt.id}>
                    {vt.name} ({vt.code})
                  </option>
                ))}
              </select>
              <div className="page-subtitle" style={{ marginTop: "4px" }}>
                Pre-selected when a sales invoice is created on the admin panel or the mobile app.
              </div>
            </div>
          </>
        ) : null}
        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
          <label>Lock Transactions On/Before</label>
          <input
            type="date"
            onClick={(e) => e.target.showPicker?.()}
            name="transactionLockDate"
            value={formData.transactionLockDate}
            onChange={handleChange}
            style={{ maxWidth: "220px" }}
          />
          <div className="page-subtitle" style={{ marginTop: "4px" }}>
            Once set, every purchase invoice, sales order, and sales invoice dated on or before this date is
            frozen — it can no longer be created, verified, or confirmed. Clear the date to remove the lock. Setting or changing it
            also checks the closing stock of every product.
          </div>
          {stockShortages ? (
            <div
              role="alert"
              style={{
                background: "var(--color-danger-soft, #fdecea)",
                color: "var(--color-danger, #b42318)",
                border: "1px solid var(--color-danger, #b42318)",
                borderRadius: 8,
                padding: "10px 14px",
                marginTop: 10,
                fontSize: 13,
              }}
            >
              <strong>Stock alert:</strong> the closing stock of {stockShortages.length} product(s) is less than the stock open invoices still need
              (or below the low-stock threshold). An SMS was sent to the company phone and the alert is listed under Alerts.
              <table style={{ marginTop: 8, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Product</th>
                    <th style={{ textAlign: "right" }}>On Hand</th>
                    <th style={{ textAlign: "right" }}>To Deliver</th>
                    <th style={{ textAlign: "right" }}>Short By / Left</th>
                  </tr>
                </thead>
                <tbody>
                  {stockShortages.map((row) => (
                    <tr key={row.productId}>
                      <td>
                        {row.skuCode} {row.productName}
                      </td>
                      <td style={{ textAlign: "right" }}>{Number(row.onHand)}</td>
                      <td style={{ textAlign: "right" }}>{Number(row.toDeliver)}</td>
                      <td style={{ textAlign: "right" }}>{row.insufficient ? `short ${Number(row.shortBy)}` : `left ${Number(row.remaining)}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
      <div className="modal-actions" style={{ justifyContent: mode === "mandatory" ? "flex-end" : "flex-end" }}>
        <button type="submit" className="primary" disabled={saving}>
          {saving ? "Saving..." : mode === "mandatory" ? "Save & Continue" : "Save Changes"}
        </button>
      </div>
    </form>
  );

  if (mode === "mandatory") {
    return (
      <div className="login-container">
        <div className="login-form" style={{ width: "520px", textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <span className="sidebar-brand-mark" style={{ width: "34px", height: "34px" }}>
              <Building2 size={17} />
            </span>
            <h2 style={{ margin: 0 }}>Complete Your Company Profile</h2>
          </div>
          <p className="login-subtitle" style={{ marginBottom: "18px" }}>
            This is a one-time setup. We use these details as the "seller" block on printed sales invoices.
          </p>
          {formBody}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4" style={{ position: "relative", maxWidth: "640px" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading profile...</div>
          </div>
        </div>
      ) : null}
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Your company's details, used as the seller block on printed sales invoices.
      </div>
      {formBody}
    </div>
  );
};

export default DistributorProfile;
